import { FC, useState } from 'react';
import { Button, Col, Form, message, Modal, Row, Tooltip, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import StatusTag from 'components/StatusTag';
import SelectSearch from 'components/SelectSearch';
import apiRoutes from 'config/apiRoute';
import { parseTaskTitle } from 'utils/string.utils';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import apiRequests from 'utils/api';
import { HiOutlineDocumentDownload } from 'react-icons/hi';

const { Text } = Typography;

interface RelateTaskRequestModalProps {
  uuid: string;
  projectUuid?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const RelateTaskRequestModal: FC<RelateTaskRequestModalProps> = ({ uuid, projectUuid, onClose, onSuccess }) => {
  const [open, setOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const confirmClickHandle = async () => {
    setSaving(true);

    try {
      const values = await form.validateFields();

      const promises = values.tasks.map((taskId: string) =>
        apiRequests.put(`${apiRoutes.PROJECT_TASKS}/${taskId}`, { add_requests: [uuid] }),
      );

      await Promise.all(promises);

      setOpen(false);

      onSuccess();

      message.success('Tasks related to the request');
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={<Text className="primary">Relate this request to a current task</Text>}
      visible={open}
      okText="Add"
      onOk={confirmClickHandle}
      confirmLoading={saving}
      onCancel={() => {
        setOpen(false);
      }}
      afterClose={() => {
        onClose();
      }}
    >
      <Form form={form} layout="vertical">
        <SelectSearch
          name="tasks"
          label="Task"
          labelKey="task_title"
          formItemClassName="mb-0"
          api={apiRoutes.PROJECT_TASKS}
          mode="multiple"
          noResponsive
          rules={[
            {
              required: true,
              message: 'This Field is required.',
            },
          ]}
          customValidator={(_, _value, items) => {
            return items.find((item: any) => item.requests_count)
              ? Promise.reject(new Error('This task is already related'))
              : Promise.resolve();
          }}
          renderOptionProps={(item: any) => ({
            disabled: item?.request !== null,
          })}
          defaultFilter={
            projectUuid
              ? {
                  filterBy: 'project.uuid',
                  value: projectUuid,
                }
              : undefined
          }
          searchKey="search_term[0][0][value]"
          defaultParams={{
            'filters[task_status]': [
              'todo',
              'in_progress',
              'waiting_approval',
              'pending',
              'approved',
              'needs_attention',
            ],
            with: 'requests_count,request',
            'search_term[0][0][value]': '',
            'search_term[0][0][field]': '*',
            'search_term[0][0][rule]': 'contains',
          }}
          tagRender={({ label, onClose: onCloseSelected }) => {
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0px 4px 0px 8px',
                  margin: '2px 4px 2px 0px',
                  border: '1px solid',
                  borderRadius: '2px',
                }}
              >
                {label}
                <Tooltip title="Close">
                  <Button
                    type="link"
                    icon={<CloseOutlined style={{ color: 'rgba(0,0,0,.45)', fontSize: 10 }} />}
                    size="small"
                    style={{ height: '22px' }}
                    onClick={onCloseSelected}
                  />
                </Tooltip>
              </div>
            );
          }}
          render={(task: any) => {
            const hasRequest = task?.request !== null;

            return (
              <div style={{ position: 'relative' }}>
                <Row
                  wrap={false}
                  justify="space-between"
                  align="middle"
                  gutter={[16, 0]}
                  style={{
                    paddingRight: 8,
                    maxWidth: 380,
                    cursor: hasRequest ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Col flex="auto">
                    <Text
                      style={{
                        color: hasRequest ? '#999' : 'inherit',
                        opacity: hasRequest ? 0.6 : 1,
                      }}
                      ellipsis
                    >
                      {parseTaskTitle(task)}
                    </Text>
                  </Col>
                  <Col flex="none" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <StatusTag
                      value={task.task_status}
                      size="small"
                      style={{
                        opacity: hasRequest ? 0.6 : 1,
                      }}
                    />
                  </Col>
                </Row>
                {hasRequest && (
                  <Tooltip title="Go to request">
                    <a
                      href={`/requests/${task.request.uuid}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                        position: 'absolute',
                        right: 8,
                        bottom: 0,
                      }}
                    >
                      <HiOutlineDocumentDownload size={14} style={{ width: 14, height: 14 }} />
                    </a>
                  </Tooltip>
                )}
              </div>
            );
          }}
        />
      </Form>
    </Modal>
  );
};

export default RelateTaskRequestModal;
